import type { Container } from "pixi.js";
import type { PresentationTaskRunner } from "../../runner/PresentationTaskRunner";
import type { SymbolWinEffect, SymbolWinAnimationRequest } from "./SymbolWinEffect";
import type { RotateTuning } from "./RotateTuning";

interface ActiveRotation {
  /** The node's rest angle (Pixi degrees, clockwise), captured before the first rotation. */
  base: number;
  gen: number;
}

/**
 * A "wobble" symbol-win animation: the winning cell rotates OUT to a peak angle then BACK to rest.
 * Composes with the scale-pulse (distinct properties). Gated by an optional `symbolIds` apply-set.
 * Tuning degrees are authored counter-clockwise; Pixi's `angle` is clockwise.
 */
export class RotateSymbolWinEffect implements SymbolWinEffect {
  private readonly tuning: RotateTuning;
  private readonly taskRunner: PresentationTaskRunner;
  private readonly symbolIds: ReadonlySet<string> | null;
  private readonly once: boolean;
  private readonly active: Map<Container, ActiveRotation> = new Map();
  private generation = 0;

  public constructor(
    tuning: RotateTuning,
    taskRunner: PresentationTaskRunner,
    options: { symbolIds?: ReadonlySet<string>; once?: boolean } = {},
  ) {
    this.tuning = tuning;
    this.taskRunner = taskRunner;
    this.symbolIds = options.symbolIds ?? null;
    this.once = options.once ?? false;
  }

  public play(request: SymbolWinAnimationRequest): void {
    if (this.symbolIds && !this.symbolIds.has(request.symbolId)) return;

    const node = request.cellNode;
    if (!node || node.destroyed) return;

    const existing = this.active.get(node);
    if (this.once && existing) return;
    const base = existing ? existing.base : node.angle;

    const gen = ++this.generation;
    this.active.set(node, { base, gen });
    void this.run(node, base, gen);
  }

  public stopAll(): void {
    for (const [node, rotation] of this.active) {
      if (node && !node.destroyed) node.angle = rotation.base;
    }
    this.active.clear();
  }

  private async run(node: Container, base: number, gen: number): Promise<void> {
    const t = this.tuning;
    const isCurrent = (): boolean => !node.destroyed && this.active.get(node)?.gen === gen;
    const setAngle = (deg: number): void => {
      if (isCurrent()) node.angle = deg;
    };

    const peak = base - t.degrees; // authored CCW → Pixi CW

    const inDelaySec = t.startDelayMilliseconds / 1000;
    if (inDelaySec > 0) {
      await this.taskRunner.sleep(inDelaySec);
      if (!isCurrent()) return;
    }

    await this.taskRunner.tweenProgress(
      t.rotateUpSeconds,
      (t01) => setAngle(base + (peak - base) * t01),
      undefined,
      { easing: t.rotateUpEasing },
    );
    if (!isCurrent()) return;

    const holdSec = t.holdSeconds ?? 0;
    if (holdSec > 0) {
      await this.taskRunner.sleep(holdSec);
      if (!isCurrent()) return;
    }

    await this.taskRunner.tweenProgress(
      t.rotateDownSeconds,
      (t01) => setAngle(peak + (base - peak) * t01),
      undefined,
      { easing: t.rotateDownEasing },
    );
  }
}
